import React, { useEffect, useState } from "react";
import {
  UncontrolledDropdown,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { Modal, Tooltip } from "antd";

import { FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import FormInput from "../../form-components/InputComponent";
import ExportToXlsx from "../../../utils/exportToXlsx";
import boqApis from "../../../api/boq/boq";
import * as XLSX from "xlsx";

const BulkBOMUpload = ({
  onUpload,
  materialCategoryOptions,
  allocation,
  budgetValue,
  existingBomLines,
  siteId,
  contractorId,
  disabled,
}: {
  onUpload: (bomLines: any[]) => void;
  materialCategoryOptions: any[];
  allocation: string;
  budgetValue: number;
  existingBomLines?: any[];
  siteId?: string;
  contractorId?: string;
  disabled?: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [response, setResponse] = useState({
    status: "",
    message: "",
  });
  const [showModal, setShowModal] = useState(false);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const files = watch("attachment");

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);


  const handleUpload = async () => {
    try {
      const attachment = files;
      if (!attachment || !attachment[0]) {
        setResponse({
          status: "error",
          message: "Please select a file",
        });
        setBlockEdit(false);
        setLoading(false);
        return;
      }
      processFile(attachment);
    } catch (error) {
      console.log("Upload error:", error);
    }
  };

  const processFile = async (attachment: any) => {
    try {
      const _file = attachment?.[0];
      const reader = new FileReader();

      reader.onload = async (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet);

          if (!jsonData || jsonData.length === 0) {
            setResponse({
              status: "error",
              message: "The uploaded file is empty or has no valid data",
            });
            setBlockEdit(false);
            setLoading(false);
            return;
          }

          if (!siteId || !contractorId) {
            setResponse({
              status: "error",
              message: "Site and Contractor must be selected before uploading",
            });
            setBlockEdit(false);
            setLoading(false);
            return;
          }

          // Call contractorBoqDropdown API to get valid BOQ materials
          const apiResponse = await boqApis.fetchContractorBoqDropdown({
            siteIds: siteId,
            contractorIds: contractorId,
          });
          const apiData = apiResponse?.data || apiResponse?.response || [];

          if (!Array.isArray(apiData) || apiData.length === 0) {
            setResponse({
              status: "error",
              message: "No BOQ materials found for the selected contractor",
            });
            setBlockEdit(false);
            setLoading(false);
            return;
          }

          // Build lookups from API response by materialId and materialName
          const apiByIdLookup: Record<string, any> = {};
          const apiByNameLookup: Record<string, any> = {};
          apiData.forEach((item: any) => {
            const matId = item.materialId?.toString() || item.materialID?.toString() || "";
            const matName = item.materialName || item.productName || "";
            if (matId) apiByIdLookup[matId] = item;
            if (matName) apiByNameLookup[matName.toLowerCase()] = item;
          });

          // Parse Excel rows and match against API response
          const matchedMaterialIds = new Set<string>();
          const validationErrors: string[] = [];
          const matchedBomLines: any[] = [];

          jsonData.forEach((row: any, index: number) => {
            const materialId = row["Material ID"]?.toString() || "";
            const materialName = row["Material Name"]?.toString() || "";
            const quantity = row["Quantity"]?.toString() || "";
            const allocated = row["Allocation"]?.toString() || "";

            // Match against API response
            const apiItem = (materialId && apiByIdLookup[materialId])
              || (materialName && apiByNameLookup[materialName.toLowerCase()])
              || null;

            if (!apiItem) {
              // Material not found in contractor BOQ - skip silently
              return;
            }

            const resolvedMatId = apiItem.materialId?.toString() || apiItem.materialID?.toString() || "";
            const budgetRemaining = apiItem.budgetRemainingQty?.toString() || apiItem.budgetRemaining?.toString() || "";
            const qtyNum = parseFloat(quantity) || 0;
            const avlQtyNum = parseFloat(budgetRemaining?.toString().replace(/,/g, "")) || 0;

            // Validate: Quantity should not exceed Avl Qty (budgetRemaining)
            if (qtyNum > avlQtyNum && avlQtyNum > 0) {
              validationErrors.push(`Row ${index + 1} (${materialName || materialId}): Quantity (${qtyNum}) exceeds Avl Qty (${avlQtyNum})`);
            }

            matchedMaterialIds.add(resolvedMatId);

            matchedBomLines.push({
              boqContractorMaterialId: apiItem.boqContractorMaterialId?.toString() || apiItem.materialID?.toString() || "",
              categoryId: apiItem.categoryId?.toString() || "",
              categoryName: apiItem.categoryName || "",
              materialId: resolvedMatId,
              materialName: apiItem.materialName || apiItem.productName || "",
              quantity: quantity,
              uomId: apiItem.uomId?.toString() || apiItem.primaryUomId?.toString() || "",
              uomName: apiItem.uomName || apiItem.primaryUomName || "",
              budgetRemaining: budgetRemaining,
              allocated: allocated,
              budgetAllocatedQty: apiItem.budgetAllocatedQty?.toString() || "",
              _fromUpload: true,
            });
          });

          if (validationErrors.length > 0) {
            setResponse({
              status: "error",
              message: validationErrors.join("; "),
            });
            setBlockEdit(false);
            setLoading(false);
            return;
          }

          // Remaining existing BOM lines that were NOT in the uploaded Excel
          const remainingBomLines = (existingBomLines || [])
            .filter((line: any) => {
              const matId = line.materialId?.toString() || "";
              return !matchedMaterialIds.has(matId);
            })
            .map((line: any) => ({
              boqContractorMaterialId: line.boqContractorMaterialId || "",
              categoryId: line.categoryId || "",
              categoryName: line.categoryName || "",
              materialId: line.materialId?.toString() || "",
              materialName: line.materialName || "",
              quantity: line.quantity || "",
              uomId: line.uomId || "",
              uomName: line.uomName || "",
              budgetRemaining: line.budgetRemaining || "",
              allocated: line.allocated || "",
              budgetAllocatedQty: line.budgetAllocatedQty || "",
            }));

          // Uploaded matched lines come first, then remaining existing lines
          const finalBomLines = [...matchedBomLines, ...remainingBomLines];

          onUpload(finalBomLines);

          setResponse({
            status: "success",
            message: "File Uploaded Successfully",
          });
        } catch (parseError) {
          console.log("Parse error:", parseError);
          setResponse({
            status: "error",
            message: "Failed to parse the uploaded file. Please check the format.",
          });
          setBlockEdit(false);
          setLoading(false);
        }
      };

      reader.onerror = () => {
        setResponse({
          status: "error",
          message: "An error occurred while reading the file",
        });
        setBlockEdit(false);
        setLoading(false);
      };

      reader.readAsArrayBuffer(_file);
    } catch (error) {
      console.log("Process file error:", error);
      setResponse({
        status: "error",
        message: "An error occurred while processing the file",
      });
      setBlockEdit(false);
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        title={disabled ? "Please select Contractor & BOM Allocation" : ""}
      >
        <UncontrolledDropdown size={"md"}>
          <div
            onClick={() => {
              if (!disabled) setShowModal(true);
            }}
            className="btn btn-white btn-outline-light"
            style={disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}}
          >
            <Icon name="upload-cloud"></Icon>
            <span>Bulk Upload BOM</span>
          </div>
        </UncontrolledDropdown>
      </Tooltip>

      <Modal
        title="Bulk BOM Upload"
        open={showModal}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse(() => ({
            status: "",
            message: "",
          }));
        }}
        width={"60%"}
      >
        <FormProvider {...methods}>
          <form>
            <p>
              You're about to upload multiple BOM lines at once. Please
              fill in the Material ID, Quantity and Allocation in the excel
              file and upload below.
            </p>

            <div className="text-right mt-3"></div>
            <p>
              <strong>Note : </strong>
              All fields in the template are mandatory. Each record must have
              unique information to avoid errors during the import process.
            </p>
            <Col md={4}>
              {files && files[0]?.name && (
                <div className="form-group m-0">
                  <div className="form-label-wrapper">
                    <label className="form-label" htmlFor={"attachment"}>
                      File Upload Area
                    </label>
                  </div>
                  <div
                    className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                    style={{ borderRadius: "3px" }}
                  >
                    <span>{files && files[0]?.name}</span>
                    {!blockEdit && (
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: `${files && files[0]?.name ? "none" : "block"}`,
                }}
              >
                <FormInput
                  type="file"
                  name="attachment"
                  label="File Upload Area"
                  placeholder="File Upload Area"
                  accept=".xlsx"
                />
              </div>
            </Col>

            <Alert
              isOpen={response?.status === "error"}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
                maxHeight: "140px",
                overflowY: "auto",
              }}
            >
              {response?.message?.includes("; ") ? (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {response.message.split("; ").map((err: string, i: number) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              ) : (
                <span>{response?.message}</span>
              )}
            </Alert>

            <Alert
              isOpen={response?.status === "success"}
              color="success"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <p>
                {response?.message}, You can{" "}
                <span
                  className="inlineTableCellLink"
                  onClick={() => {
                    setShowModal(false);
                    setBlockEdit(false);
                    setValue("attachment", null);
                    setLoading(false);
                    setResponse(() => ({
                      status: "",
                      message: "",
                    }));
                  }}
                >
                  close
                </span>{" "}
                this popup
              </p>
            </Alert>

            <Row className="mt-4">
              <Col>
                {response?.status == "success" ? (
                  <></>
                ) : (
                  <ul className="actions clearfix d-flex">
                    <li>
                      {loading ? (
                        <Button color="primary" type="button" size="md">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          type="button"
                          disabled={!files?.[0]}
                          onClick={() => {
                            setResponse(() => ({
                              status: "",
                              message: "",
                            }));
                            setBlockEdit(true);
                            setLoading(true);
                            handleUpload();
                          }}
                        >
                          Upload
                        </Button>
                      )}
                    </li>
                    {!loading && (
                      <li className="m-3 mt-0">
                        <Button
                          color="primary"
                          onClick={() => {
                            setShowModal(false);
                            setValue("attachment", null);
                          }}
                          className="btn-dim btn btn-secondary"
                        >
                          Cancel
                        </Button>
                      </li>
                    )}
                  </ul>
                )}
              </Col>
            </Row>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default BulkBOMUpload;
